import { Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { monetaryFormat } from '@/utils/monetary-format';
import type { MappedFeature } from '../utils/map-graphql-to-calculator.util';

interface EnumAddOnRowProps {
    feature: MappedFeature;
    selectedValue: string;
    currency?: { symbol: string } | null;
    onChange: (code: string, value: string) => void;
    disabled?: boolean;
}

export const EnumAddOnRow = ({ feature, selectedValue, currency, onChange, disabled }: EnumAddOnRowProps) => {
    const { t } = useTranslation();
    const options = feature.addOnOptions ?? [];
    const hasIncluded = feature.displayValue !== '';
    const baseIndex = hasIncluded ? options.findIndex((o) => o.value === feature.displayValue) : -1;

    const currentOption =
        options.find((o) => o.value === selectedValue) ??
        (hasIncluded ? options.find((o) => o.value === feature.displayValue) : undefined);
    const price = currentOption?.price ?? 0;
    const effectiveValue = selectedValue || '';
    const nothingSelected = !options.some((o) => o.value === effectiveValue);

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr auto', md: '1fr 220px 140px' },
                gridTemplateRows: { xs: 'auto auto', md: '1fr' },
                alignItems: 'center',
                py: 1.5,
                px: 2,
                borderRadius: 2,
                bgcolor: 'action.hover',
                gap: { xs: 1, md: 2 },
            }}
        >
            <Box sx={{ minWidth: 0, gridColumn: { xs: '1 / -1', md: 'auto' } }}>
                <Typography variant="body2" fontWeight={600}>
                    {t(feature.label)}
                </Typography>
                {feature.description && (
                    <Typography variant="caption" color="text.secondary" display="block">
                        {t(feature.description)}
                    </Typography>
                )}
                <Typography variant="caption" color={hasIncluded ? 'success.main' : 'text.secondary'}>
                    {hasIncluded
                        ? t('billing.calculator.addon.included-value', {
                              value: t(options.find((o) => o.value === feature.displayValue)?.label ?? ''),
                          })
                        : t('billing.calculator.addon.not-included-in-plan')}
                </Typography>
            </Box>

            <Select
                size="small"
                value={effectiveValue}
                displayEmpty
                disabled={!!disabled}
                onChange={(e) => onChange(feature.code, e.target.value)}
                sx={{ fontSize: '0.875rem', width: '100%' }}
                renderValue={
                    nothingSelected
                        ? () => (
                              <em style={{ opacity: 0.5 }}>{t('billing.calculator.addon.select-option')}</em>
                          )
                        : undefined
                }
            >
                {options.map((opt, idx) => {
                    const isDowngrade = baseIndex !== -1 && idx < baseIndex;
                    return (
                        <MenuItem key={opt.value} value={opt.value} disabled={isDowngrade}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                width="100%"
                                spacing={2}
                            >
                                <span>{t(opt.label)}</span>
                                {!isDowngrade && opt.value !== feature.displayValue && (
                                    <Typography variant="caption" color="text.secondary">
                                        {monetaryFormat(opt.price, currency)}
                                        {t('billing.calculator.per-month')}
                                    </Typography>
                                )}
                            </Stack>
                        </MenuItem>
                    );
                })}
            </Select>

            <Box sx={{ textAlign: 'right' }}>
                {nothingSelected ? (
                    <Typography variant="body2" color="text.disabled">
                        —
                    </Typography>
                ) : price > 0 && effectiveValue !== feature.displayValue ? (
                    <>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {t('billing.calculator.addon.upgrade-cost')}
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="primary.main">
                            +{monetaryFormat(price, currency)}
                            {t('billing.calculator.per-month')}
                        </Typography>
                    </>
                ) : (
                    <Typography variant="body2" color="text.disabled">
                        {t('billing.calculator.addon.included')}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};
